import { DECIMAL_NUMBER_REGX, US_CURRENCY_FORMAT } from "../constants";

const formatCurrency = (number, formatType, formatDecimal = false) => {
  const hasDecimal = number.indexOf(".") > -1;
  if (!hasDecimal) {
    return new Intl.NumberFormat(formatType).format(number);
  }
  const [integerPortion, decimalPortion] = number.split(".");
  const formattedIntegerPortion = new Intl.NumberFormat(formatType).format(
    integerPortion
  );
  if (formatDecimal) {
    const decimal =
      decimalPortion?.length === 1 ? `${decimalPortion}0` : decimalPortion;
    return `${formattedIntegerPortion}.${decimal}`;
  }

  return `${formattedIntegerPortion}.${decimalPortion}`;
};
const  getFxRateData = (exchangeData) => {
  return exchangeData?.transaction_wise_fx_rate_v2 || exchangeData?.transaction_wise_fx_rate || {};
}
const handleBlendedFxRates = (
  inputAmount,
  exchangeData,
  currencyType
) => {
  const breakdown = [];
  let unlockInfo = null;
  currencyType = currencyType || "USD";

  // Pick new field if available, else fallback to old one
  const {
    transaction_wise_fx_rate_v2,
    transaction_wise_fx_rate,
    max_amount_transaction,
  } = exchangeData || {};

  const fxRates = transaction_wise_fx_rate_v2 || transaction_wise_fx_rate;
  if (!fxRates) return { breakdown, unlockInfo };

  const convertToNumber = (amount) =>
    parseFloat(amount?.toString().replace(/,/g, ""));

  let totalInput = convertToNumber(convertToNumber(inputAmount).toFixed(2));
  let remaining = totalInput;

  if (!DECIMAL_NUMBER_REGX.test(totalInput)) return { breakdown, unlockInfo };

  const keys = Object.keys(fxRates);

  // ---------- CASE 1: New v2 schema ----------
  if (transaction_wise_fx_rate_v2) {
    // Separate TIERED and SLABS
    const tieredKey = keys.find(
      (k) => fxRates[k].campaign_calculation_type === "TIERED",
    );
    const slabs = keys.filter(
      (k) => fxRates[k].campaign_calculation_type !== "TIERED",
    );

    // Apply TIERED first
    if (tieredKey) {
      const tiered = fxRates[tieredKey];
      const tieredMax = tiered.amount;
      let tempremaining = remaining;
      if (currencyType == "INR") {
        tempremaining = parseFloat(
          (remaining / tiered.effective_exchange_rate).toFixed(2),
        );
      }
      let applyAmount = 0;
      if (tempremaining >= tieredMax) {
        applyAmount = tieredMax;
      } else {
        applyAmount = tempremaining;
      }

      if (applyAmount > 0) {
        breakdown.push({
          source_amount: parseFloat(applyAmount.toFixed(2)),
          destination_amount: parseFloat(
            (applyAmount * tiered.effective_exchange_rate).toFixed(2),
          ),
          effective_fx_rate: tiered.effective_exchange_rate,
          campaign: "Promotional FX Rate",
          campaign_id: tiered.campaign_id,
          display_effective_rate:
            tiered.effective_exchange_rate_data.display_value,
          display_source_amount: removeDecimal(
            parseFloat(applyAmount.toFixed(2)),
            US_CURRENCY_FORMAT,
          ),
          promotional_applicable: true,
        });
        if (currencyType == "INR") {
          remaining -= applyAmount * tiered.effective_exchange_rate;
        } else {
          remaining -= applyAmount;
        }
      }
    }

    // Apply slab if still remaining
    if (remaining > 0 && slabs.length > 0) {
      let eligibleSlabs = [];
      if (currencyType == "INR") {
        eligibleSlabs = slabs
          .map((k) => fxRates[k])
          .filter((s) => totalInput / s.effective_exchange_rate >= s.amount);
      } else {
        eligibleSlabs = slabs
          .map((k) => fxRates[k])
          .filter((s) => totalInput >= s.amount);
      }

      if (eligibleSlabs.length > 0) {
        const slab = eligibleSlabs.sort((a, b) => b.amount - a.amount)[0];
        let sourceAmount = parseFloat(remaining.toFixed(2));
        if (currencyType == "INR") {
          sourceAmount = parseFloat(
            (remaining / slab.effective_exchange_rate).toFixed(2),
          );
        }
        breakdown.push({
          source_amount: sourceAmount,
          destination_amount: parseFloat(
            (remaining * slab.effective_exchange_rate).toFixed(2),
          ),
          effective_fx_rate: slab.effective_exchange_rate,
          campaign: "Best FX Guaranteed",
          campaign_id: slab.campaign_id,
          display_effective_rate:
            slab.effective_exchange_rate_data.display_value,
          display_source_amount: removeDecimal(
            parseFloat(sourceAmount.toFixed(2)),
            US_CURRENCY_FORMAT,
          ),
          promotional_applicable: false,
        });
      }
    }

    // unlockInfo & average rate
    let nextSlab = null;
    let nextCompleteSlab = null;
    let averageRate = 0;

    if (breakdown.length > 0) {
      const totalSource = breakdown.reduce(
        (sum, item) => sum + item.source_amount,
        0,
      );
      const totalDest = breakdown.reduce(
        (sum, item) => sum + item.destination_amount,
        0,
      );
      averageRate = totalSource > 0 ? totalDest / totalSource : 0;
    }

    const round2 = (num) => parseFloat(num.toFixed(2));
    if (remaining > 0 && slabs.length > 0) {
      const slabsArray = slabs.map((k) => fxRates[k]);
      const nextSlabArray = slabsArray.sort((a, b) => a.amount - b.amount);

      // Convert totalInput to USD for comparison if currency is INR
      let comparisonAmount = totalInput;
      if (currencyType == "INR") {
        // Use the first available slab rate for conversion, or fallback to 1
        const conversionRate =
          nextSlabArray.length > 0
            ? nextSlabArray[0].effective_exchange_rate
            : 1;
        comparisonAmount = totalInput / conversionRate;
      }

      for (let i = 0; i < nextSlabArray.length; i++) {
        if (comparisonAmount >= nextSlabArray[i].threshold_value) {
          nextSlab = nextSlabArray[i];
        }
      }
      for (let i = 0; i < nextSlabArray.length; i++) {
        if (
          comparisonAmount >= nextSlabArray[i].amount &&
          comparisonAmount >= nextSlabArray[i].threshold_value
        ) {
          nextCompleteSlab = nextSlabArray[i];
        }
      }
      if (nextSlab && nextSlab.amount >= comparisonAmount) {
        unlockInfo = {
          amountToUnlock: nextSlab.amount - comparisonAmount,
          unlockRate: nextSlab.effective_exchange_rate,
          unlockThreshold: nextSlab.amount,
          promoUnlocked: false,
          averageRate: round2(averageRate),
        };
        sessionStorage.setItem("averageRateV2", unlockInfo.averageRate);
      } else if (nextCompleteSlab && sessionStorage.getItem("averageRateV2")) {
        unlockInfo = {
          amountToUnlock: nextCompleteSlab.amount - comparisonAmount,
          unlockRate: nextCompleteSlab.effective_exchange_rate,
          unlockThreshold: nextCompleteSlab.amount,
          promoUnlocked: true,
          averageRate: sessionStorage.getItem("averageRateV2"),
        };
      }
    }
  }

  // ---------- CASE 2: Old schema ----------
  else if (transaction_wise_fx_rate) {
    const txnWiseKeys = Object.keys(fxRates || {});
    const len = txnWiseKeys?.length;
    const lastTxnWiseKey = txnWiseKeys[len - 1];

    for (let key in fxRates) {
      let {
        max_amount_transaction: max_amount,
        max_amount_transaction,
        promotional_applicable,
        effective_exchange_rate_data: {
          display_value: display_effective_exchange_rate,
          value: effective_exchange_rate,
        } = {},
      } = fxRates[key] || {};

      if (remaining > 0) {
        if (currencyType === "USD") {
          if (totalInput <= max_amount_transaction) {
            if (key === lastTxnWiseKey) {
              breakdown.push({
                source_amount: parseFloat(remaining.toFixed(2)),
                destination_amount: parseFloat(
                  (remaining * effective_exchange_rate).toFixed(2),
                ),
                effective_fx_rate: effective_exchange_rate,
                campaign: promotional_applicable
                  ? "Promotional FX Rate"
                  : "Best FX Guaranteed",
                campaign_id: fxRates[key].campaign_id,
                display_effective_rate: display_effective_exchange_rate,
                display_source_amount: removeDecimal(
                  parseFloat(remaining.toFixed(2)),
                  US_CURRENCY_FORMAT,
                ),
                promotional_applicable,
              });
              remaining = 0;
              break;
            }
            breakdown.push({
              source_amount:
                remaining <= max_amount
                  ? parseFloat(remaining.toFixed(2))
                  : parseFloat(max_amount.toFixed(2)),
              destination_amount:
                remaining <= max_amount
                  ? parseFloat((remaining * effective_exchange_rate).toFixed(2))
                  : parseFloat(
                      (max_amount * effective_exchange_rate).toFixed(2),
                    ),
              effective_fx_rate: effective_exchange_rate,
              campaign: promotional_applicable
                ? "Promotional FX Rate"
                : "Best FX Guaranteed",
              campaign_id: fxRates[key].campaign_id,
              display_effective_rate: display_effective_exchange_rate,
              display_source_amount:
                remaining <= max_amount
                  ? removeDecimal(
                      parseFloat(remaining.toFixed(2)),
                      US_CURRENCY_FORMAT,
                    )
                  : removeDecimal(
                      parseFloat(max_amount.toFixed(2)),
                      US_CURRENCY_FORMAT,
                    ),
              promotional_applicable,
            });
            remaining = max_amount >= remaining ? 0 : remaining - max_amount;
          }
        } else {
          if (key === lastTxnWiseKey) {
            breakdown.push({
              source_amount: parseFloat(
                (remaining / effective_exchange_rate).toFixed(2),
              ),
              destination_amount: parseFloat(remaining.toFixed(2)),
              effective_fx_rate: effective_exchange_rate,
              campaign: promotional_applicable
                ? "Promotional FX Rate"
                : "Best FX Guaranteed",
              campaign_id: fxRates[key].campaign_id,
              display_effective_rate: display_effective_exchange_rate,
              display_source_amount: removeDecimal(
                parseFloat((remaining / effective_exchange_rate).toFixed(2)),
                US_CURRENCY_FORMAT,
              ),
              promotional_applicable,
            });
            remaining = 0;
            break;
          }
          breakdown.push({
            source_amount: parseFloat(
              (
                Math.min(max_amount * effective_exchange_rate, remaining) /
                effective_exchange_rate
              ).toFixed(2),
            ),
            destination_amount: parseFloat(
              Math.min(max_amount * effective_exchange_rate, remaining).toFixed(
                2,
              ),
            ),
            effective_fx_rate: effective_exchange_rate,
            campaign: promotional_applicable
              ? "Promotional FX Rate"
              : "Best FX Guaranteed",
            campaign_id: fxRates[key].campaign_id,
            display_effective_rate: display_effective_exchange_rate,
            display_source_amount: removeDecimal(
              parseFloat(
                (
                  Math.min(max_amount * effective_exchange_rate, remaining) /
                  effective_exchange_rate
                ).toFixed(2),
              ),
              US_CURRENCY_FORMAT,
            ),
            promotional_applicable,
          });
          remaining =
            max_amount * effective_exchange_rate >= remaining
              ? 0
              : remaining - max_amount * effective_exchange_rate;
        }
      }
    }
  }

  return { breakdown, unlockInfo };
};

const removeDecimal = (amount, formatType) => {
  if (amount || amount === 0) {
    const convertedAmount = parseFloat(amount?.toString().replace(/,/g, ""));
    const formattedAmount = formatCurrency(
      `${convertedAmount.toLocaleString("fullwide", { useGrouping: false })}`,
      formatType,
      true
    );
    return formattedAmount;
  }
};

export { formatCurrency, removeDecimal, handleBlendedFxRates, getFxRateData };
